import { Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import { useOverride } from '@override';

import { Batch, BatchTypeEnum, TemplateType } from '@e-origin/shared';

import { queryClient } from '@hooks/queryClient';
import { useSettings } from '@hooks/useSettings';
import { RiskAnalysis } from '../declarations';
import { BatchAudit } from './BatchAudit';
import { BulkUpdateBatch } from './BulkUpdateBatch/BulkUpdateBatch';
import { CustomsMessageSimulator } from './CustomsMessageSimulator/CustomsMessageSimulator';
import { FailedBatchDetails } from './FailedBatchDetails';
import { FinalizeBatch } from './FinalizeBatch';
import { GenerateProformaB2BInvoice } from './GenerateProformaB2BInvoice';
import { InvalidateBatch } from './InvalidateBatch';
import { SendBatch } from './SendBatch';
import { UpdateBatch } from './UpdateBatch';

export const useBatchModalManager = () => {
  const Override = useOverride();
  const { data: settings } = useSettings();

  return {
    openNewBatch: (props: { batchType: BatchTypeEnum; afterCreate: () => void; isUpdate?: boolean; batch?: Batch; title?: string }) => {
      modals.open({
        title: props.title ?? (props.isUpdate ? 'Update Batch' : 'New Batch'),
        size: settings.isForwarder ? 'xl' : 'lg',
        children: (
          <Override.NewBatch
            onHide={modals.closeAll}
            afterCreate={props.afterCreate}
            batchType={props.batchType}
            isUpdate={props.isUpdate || false}
            updatedBatch={props.batch}
          />
        ),
      });
    },
    openUpdateBatch: (props: { batch: Batch; batchType: BatchTypeEnum; afterCreate: () => void }) => {
      const { batch, batchType, afterCreate } = props;
      modals.open({
        title: 'Update Batch',
        children: (
          <UpdateBatch
            isHighValue={batch?.templateType === TemplateType.HIGH_VALUE_H1 || false}
            batch={batch}
            batchType={batchType}
            onHide={modals.closeAll}
            afterCreate={afterCreate}
          />
        ),
        size: 'lg',
      });
    },
    openInvalidateBatch: (props: { batch: Batch; afterCreate: () => void }) => {
      modals.open({
        title: 'Invalidate batch',
        children: <InvalidateBatch afterCreate={props.afterCreate} onHide={modals.closeAll} updatedBatch={props.batch} />,
        size: 'lg',
      });
    },
    openBulkUpdateBatch: (props: { batch: Batch; afterCreate: () => void }) => {
      modals.open({
        title: 'Bulk Update',
        children: <BulkUpdateBatch afterCreate={props.afterCreate} onHide={modals.closeAll} updatedBatch={props.batch} />,
        size: 'xl',
      });
    },
    openFinalizeBatch: (props: { batch: Batch; afterFinalize: () => void }) => {
      modals.open({
        size: '60vw',
        withCloseButton: false,
        onClose: async () => {
          await queryClient.invalidateQueries({ queryKey: ['retrieve-declaration-finalize'] });
        },
        children: (
          <FinalizeBatch
            batch={props.batch}
            onHide={async () => {
              modals.closeAll();
              props.afterFinalize();
            }}
          />
        ),
      });
    },
    openDeleteBatch: (props: { batch: Batch; onConfirm: () => void }) => {
      modals.openConfirmModal({
        title: 'Confirm batch delete',
        children: (
          <Text>
            Are you sure you want to delete the batch? <br />
            This action cannot be undone!
          </Text>
        ),
        labels: { confirm: 'Delete', cancel: 'Cancel' },
        confirmProps: { color: 'red' },
        onConfirm: props.onConfirm,
      });
    },
    openArchiveBatch: (props: { isUnarchive: boolean; onConfirm: () => void }) => {
      const { isUnarchive, onConfirm } = props;
      modals.openConfirmModal({
        title: isUnarchive ? 'Confirm batch un-archive' : 'Confirm batch archive',
        children: <Text>{isUnarchive ? 'Are you sure you want to un-archive the batch?' : 'Are you sure you want to archive the batch?'}</Text>,
        labels: { confirm: isUnarchive ? 'Un-archive' : 'Archive', cancel: 'Cancel' },
        onConfirm,
      });
    },
    openFailedDetails: (props: { batch: Batch }) => {
      modals.open({
        title: 'Failed Batch Details',
        children: <FailedBatchDetails batch={props.batch} onHide={modals.closeAll} />,
        size: 'lg',
      });
    },
    openSendBatch: (props: { batchId: string }) => {
      modals.open({
        size: 'lg',
        withCloseButton: false,
        children: <SendBatch batchId={props.batchId} title="Send Batch" onHide={modals.closeAll} />,
      });
    },
    openRiskAnalysis: (props: { batch: Batch }) => {
      modals.open({
        size: '80vw',
        withCloseButton: false,
        children: <RiskAnalysis batch={props.batch} onHide={modals.closeAll} />,
      });
    },
    openGenerateInvoice: (props: { batch: Batch; onConfirm: () => void }) => {
      modals.openConfirmModal({
        title: 'Generate Invoice',
        children: <Text>Do you really want to generate the invoice?</Text>,
        labels: { confirm: 'Yes', cancel: 'Cancel' },
        onConfirm: props.onConfirm,
      });
    },
    openSendToBroker: (props: { batch: Batch; onConfirm: () => void }) => {
      modals.openConfirmModal({
        title: `Send to broker ${(props.batch.broker as any)?.name || ''}`,
        children: <Text>Are you sure you want to send this batch to the broker?</Text>,
        labels: { confirm: 'Send', cancel: 'Cancel' },
        onConfirm: props.onConfirm,
        centered: true,
      });
    },
    openGenerateProformaB2BInvoice: (props: { batch: Batch }) => {
      modals.open({
        size: 'lg',
        withCloseButton: false,
        children: <GenerateProformaB2BInvoice batch={props.batch} onHide={modals.closeAll} />,
      });
    },
    openRunRiskAnalysisCalculations: (props: { batch: Batch }) => {
      modals.open({
        size: 'xl',
        title: 'Run Risk Analysis Calculations',
        children: <Override.RunRiskAnalysisCalculations batch={props.batch} onHide={modals.closeAll} />,
      });
    },
    openBatchAudit: (props: { batch: Batch }) => {
      modals.open({
        size: 'xl',
        title: 'Batch Audit',
        children: <BatchAudit batch={props.batch} onHide={modals.closeAll} />,
      });
    },
    openSimulatorMessages: (props: { batch: Batch }) => {
      modals.open({
        size: '80vw',
        title: 'Simulate Customs Messages',
        children: <CustomsMessageSimulator batch={props.batch} onHide={modals.closeAll} />,
      });
    },
  };
};
